/**
 * BANK-QUESTION-MANAGE-MODAL
 * Opened from the pencil icon (or now, the row itself — 2026-09-17, Ben) on a "Banque de
 * questions" row (/supervisor/benchmark) — view/dissociate the known phrasings of a
 * bank_questions row (grouped by language, KB §36 — a live app.Questions row carries all its
 * languages in one `name` jsonb, not one row per language, so a good French scan match usually
 * has en/de/es siblings worth capturing too, not just fr), or delete the whole row. Moved out of
 * an inline expandable panel into its own modal to make room for more fields/actions later
 * without cramming them into the list row.
 *
 * Restructured into tabs (2026-09-17, Ben's feedback after testing) — "Textes liés" (unchanged
 * content), "Exclusions" (new — scan suggestions marked "not a match" so they stop coming back),
 * "Utilisations" (templates using one of this row's phrasings). The calc_benchmarks recap is a
 * per-sector bar chart above the tabs, not a tab. Tabs component reused as-is
 * (src/components/tabs.tsx, already used elsewhere in the app).
 */

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { motion } from "framer-motion"
import { useAppDispatch } from "@/core/root-reducer"
import Modal from "@/components/modal"
import ModalConfirm from "@/components/modal-confirm"
import LoadingModal from "@/modals/loading.modal"
import CongratulationModal from "@/modals/congratulation.modal"
import Link from "@/components/link"
import Button from "@/components/button"
import Card from "@/components/card"
import Chip from "@/components/chip"
import Checkbox from "@/components/checkbox"
import Tabs from "@/components/tabs"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import ListButton from "@/components/list-button"
import Tooltip from "@/components/tooltip"
import {
  faCalculator,
  faTrash,
  faUnlink,
  faMagnifyingGlass,
  faLink,
  faStar,
  faCircleNotch,
  faBan,
  faRotateLeft,
  faLayerGroup,
  faBuilding
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { SECTOR_ICONS } from "@/features/benchmark/entities/question/ui/modals/SectorGrid"
import BankQuestionTagChips from "@/features/benchmark/entities/question/ui/BankQuestionTagChips"
import BusyOverlay from "@/features/benchmark/entities/question/ui/BusyOverlay"
import { filterBankScanSuggestions } from "@/features/benchmark/entities/question/model/filterBankScanSuggestions.utils"
import {
  fetchBankQuestionAllTexts_AsSupervisor,
  unlinkTextFromBankQuestion_AsSupervisor,
  deleteBankQuestion_AsSupervisor,
  linkTextToBankQuestion_AsSupervisor,
  calculateOneBankQuestion_AsSupervisor,
  scanBankQuestionSimilar_AsSupervisor,
  setPrimaryBankQuestionText_AsSupervisor,
  excludeBankQuestionSuggestion_AsSupervisor,
  removeBankQuestionExclusion_AsSupervisor,
  fetchBankQuestionBenchmarkSummary_AsSupervisor,
  fetchBankQuestionTemplateUsage_AsSupervisor,
  BankScanSuggestion,
  BankQuestionBenchmarkSummaryRow,
  BankQuestionTemplateUsage
} from "@/features/benchmark/entities/question/model/bankQuestion.thunk"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVING } from "@/redux/_status.types"
import { QUESTION_LIST_SPECIAL } from "@/redux/question.types"
import {
  BANK_QUESTION_LANGUAGES as LANGUAGES,
  BANK_QUESTION_LANGUAGE_LABELS as LANGUAGE_LABELS,
  BANK_QUESTION_LANGUAGE_FLAGS as LANGUAGE_FLAGS
} from "@/features/benchmark/entities/question/model/bankQuestionLanguages"

//No existing sector color palette anywhere in the app to reuse (checked — SectorGrid/the accounts
//sector filter are icon-only, no color) — this one's local to this strip, distinguishing the 11
//sectors + "all" now that their names are hidden behind an icon+tooltip.
const SECTOR_COLORS: Record<string, string> = {
  all: "#495057",
  tech: "#74C0FC",
  commerce: "#FFA94D",
  industry: "#A9B4BE",
  healthcare: "#FF8787",
  education: "#66D9E8",
  services: "#B197FC",
  media: "#FFD43B",
  public: "#748FFC",
  associations: "#F783AC",
  housing: "#69DB7C",
  other: "#CED4DA"
}

//bank_questions.tags is seeded as-is from dim_questions.special (KB §9 "tags incohérents gardés
//tels quels") — raw Karasek/nps slugs, not meant for display. QUESTION_LIST_SPECIAL already maps
//the Karasek ones to a translation key elsewhere in the app (RPS dashboard); reused here rather
//than re-typing the same 6 labels. "nps" isn't in that list but has its own top-level key.
function tagLabel(t: (key: string) => string, tag: string) {
  const special = QUESTION_LIST_SPECIAL.find((s) => s.id === tag)
  if (special) return t(special.name)
  // i18next falls back to the key itself (the raw tag) when there's no translation for it —
  // exactly the desired fallback, no explicit defaultValue needed.
  return t(tag)
}

type Tab = "links" | "exclusions" | "usages"

interface Props {
  bankId: string
  title: string
  tags: string[] | null
  isReverse: boolean
  scalePoints: number | null
  onClose: () => void
  onChanged: () => void //Refetch the list after a successful delete or set-primary (its
  //displayed text/counts can change)
}

//The same fr text can come back twice, once on a 10-point scale and once on another one
function scanKey(sug: BankScanSuggestion) {
  return `${sug.texts.fr}|${sug.scalePoints === 10}`
}

function BankQuestionManageModal(props: Props) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState<Tab>("links")
  const [isLoaded, setIsLoaded] = useState(false)
  const [texts, setTexts] = useState<Record<string, string[]>>({})
  const [excludedTexts, setExcludedTexts] = useState<Record<string, string[]>>({})
  const [benchmarkSummary, setBenchmarkSummary] = useState<BankQuestionBenchmarkSummaryRow[]>([])
  const [templateUsages, setTemplateUsages] = useState<BankQuestionTemplateUsage[]>([])
  const [isTemplateUsagesLoaded, setIsTemplateUsagesLoaded] = useState(false)
  //A widely-used question (a standard Karasek item) can have 300+ usages — capped by default,
  //same "afficher plus" pattern as the scan suggestions list.
  const [showAllUsages, setShowAllUsages] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isScanOpen, setIsScanOpen] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [scanResults, setScanResults] = useState<BankScanSuggestion[]>([])
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([])
  const checkedSuggestions = scanResults.filter((sug) => selectedSuggestions.includes(scanKey(sug)))
  //Blocks every other action while one is in flight — associate/merge in particular calls one
  //request per language sequentially and can take a moment; nothing else here should be
  //clickable in the meantime (Tom, 2026-09-14).
  const [isBusy, setIsBusy] = useState(false)
  const [isCalculating, setIsCalculating] = useState(false)
  const [showCalculationSuccess, setShowCalculationSuccess] = useState(false)
  const [pendingCalculations, setPendingCalculations] = useState<Record<string, boolean>>({})
  const needsCalculation = !!pendingCalculations[props.bankId]

  useEffect(() => {
    // Every per-row state is reset here, and a late response for a previous bankId is ignored
    // instead of overwriting the current one.
    let isCurrent = true
    setIsLoaded(false)
    setBenchmarkSummary([])
    setTemplateUsages([])
    setIsTemplateUsagesLoaded(false)
    setShowAllUsages(false)
    setShowDeleteConfirm(false)
    setIsScanOpen(false)
    setIsScanning(false)
    setScanResults([])
    setSelectedSuggestions([])
    dispatch(fetchBankQuestionAllTexts_AsSupervisor(props.bankId))
      .unwrap()
      .then((res) => {
        if (!isCurrent) return
        setTexts(res.texts)
        setExcludedTexts(res.excludedTexts)
        setIsLoaded(true)
      })
    dispatch(fetchBankQuestionBenchmarkSummary_AsSupervisor(props.bankId))
      .unwrap()
      .then((res) => isCurrent && setBenchmarkSummary(res.rows))
    dispatch(fetchBankQuestionTemplateUsage_AsSupervisor(props.bankId))
      .unwrap()
      .then((res) => isCurrent && setTemplateUsages(res.usages))
      .finally(() => isCurrent && setIsTemplateUsagesLoaded(true))
    return () => {
      isCurrent = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.bankId])

  //Total across every language, not just the one being edited — a row with texts.fr but no
  //texts.en yet is normal, not an orphan risk; only removing the very last phrasing overall is.
  const totalTexts = Object.values(texts).reduce((sum, arr) => sum + arr.length, 0)

  async function handleUnlink(lang: string, text: string) {
    setIsBusy(true)
    try {
      const res = await dispatch(unlinkTextFromBankQuestion_AsSupervisor({ bankId: props.bankId, text, lang })).unwrap()
      if (res.error || res.removed === 0) return // fetchBackend already toasted a real error;
      // removed === 0 means the server refused (last remaining phrasing overall) — nothing to reflect locally

      setTexts((prev) => ({ ...prev, [lang]: (prev[lang] || []).filter((t) => t !== text) }))
      toast(t("benchmark_editor_supervisor_unlinked_toast"))

      setPendingCalculations((prev) => ({ ...prev, [props.bankId]: true }))
      props.onChanged()
    } finally {
      setIsBusy(false)
    }
  }

  //Moves `text` to index 0 — listBankQuestions() displays texts.fr[0] as the row's representative
  //text in the "Banque de questions" table (KB §36), this is how a supervisor picks which
  //phrasing shows there instead (e.g. preferring the clean wording over a numbered template
  //variant like "10. ...").
  async function handleSetPrimary(lang: string, text: string) {
    setIsBusy(true)
    try {
      const res = await dispatch(setPrimaryBankQuestionText_AsSupervisor({ bankId: props.bankId, text, lang })).unwrap()
      if (res.error || !res.success) return // fetchBackend already toasted a real error

      setTexts((prev) => ({ ...prev, [lang]: [text, ...(prev[lang] || []).filter((t) => t !== text)] }))
      props.onChanged()
    } finally {
      setIsBusy(false)
    }
  }

  async function handleScan() {
    if (isScanOpen) {
      setIsScanOpen(false)
      return
    }
    setActiveTab("links")
    setIsScanOpen(true)
    setIsScanning(true)
    setIsBusy(true)
    try {
      const res = await dispatch(scanBankQuestionSimilar_AsSupervisor(props.bankId)).unwrap()
      let suggestions = res.suggestions
      try {
        suggestions = await filterBankScanSuggestions(props.title, suggestions)
      } catch {
        toast(t("benchmark_editor_supervisor_scan_filter_unavailable"), { type: "warning" })
      }
      setScanResults(suggestions)
      setSelectedSuggestions([])
    } finally {
      setIsScanning(false)
      setIsBusy(false)
    }
  }

  function toggleSuggestion(sug: BankScanSuggestion) {
    setSelectedSuggestions((prev) => prev.includes(scanKey(sug))
      ? prev.filter((key) => key !== scanKey(sug))
      : [...prev, scanKey(sug)])
  }

  //One link call per language present on this match (fr always, en/de/es whenever that live
  //question's own name jsonb has them) — captures the whole multi-language set in one click
  //instead of only the fr text.
  async function handleAssociate(suggestions: BankScanSuggestion | BankScanSuggestion[]) {
    setIsBusy(true)
    let hasChanges = false
    try {
      for (const sug of Array.isArray(suggestions) ? suggestions : [suggestions]) {
        const added: Record<string, string> = {}
        for (const [lang, text] of Object.entries(sug.texts)) {
          const res = await dispatch(linkTextToBankQuestion_AsSupervisor({ bankId: props.bankId, text, lang })).unwrap()
          if (res.error) continue
          if (res.conflictBankId) {
            toast(t("benchmark_editor_supervisor_link_conflict_toast"), { type: "error" })
            continue
          }
          added[lang] = text
        }
        if (Object.keys(added).length === 0) continue
        hasChanges = true
        setTexts((prev) => {
          const next = { ...prev }
          for (const [lang, text] of Object.entries(added)) next[lang] = [...new Set([...(next[lang] || []), text])]
          return next
        })
        setScanResults((prev) => prev.filter((s) => s.texts.fr !== sug.texts.fr))
        setSelectedSuggestions((prev) => prev.filter((key) => !key.startsWith(sug.texts.fr + "|")))
        setPendingCalculations((prev) => ({ ...prev, [props.bankId]: true }))
      }
      if (hasChanges) toast(t("benchmark_editor_supervisor_associated_toast"))
    } finally {
      if (hasChanges) props.onChanged()
      setIsBusy(false)
    }
  }

  async function handleCalculate() {
    setIsBusy(true)
    setIsCalculating(true)
    setShowCalculationSuccess(false)
    try {
      const res = await dispatch(calculateOneBankQuestion_AsSupervisor(props.bankId)).unwrap()
      if (res.error) return
      setPendingCalculations((prev) => ({ ...prev, [props.bankId]: false }))
      const summaryRes = await dispatch(fetchBankQuestionBenchmarkSummary_AsSupervisor(props.bankId)).unwrap()
      setBenchmarkSummary(summaryRes.rows)
      props.onChanged()
      setShowCalculationSuccess(true)
    } finally {
      setIsCalculating(false)
      setIsBusy(false)
    }
  }

  //"Pas la même question" (2026-09-17, Ben) — scanBankQuestionSimilar_AsSupervisor stops
  //offering this fr text again for this row afterwards.
  async function handleExclude(suggestions: BankScanSuggestion | BankScanSuggestion[]) {
    setIsBusy(true)
    try {
      for (const sug of Array.isArray(suggestions) ? suggestions : [suggestions]) {
        const res = await dispatch(excludeBankQuestionSuggestion_AsSupervisor({ bankId: props.bankId, text: sug.texts.fr })).unwrap()
        if (res.error || !res.success) continue
        setScanResults((prev) => prev.filter((s) => s.texts.fr !== sug.texts.fr))
        setSelectedSuggestions((prev) => prev.filter((key) => !key.startsWith(sug.texts.fr + "|")))
        setExcludedTexts((prev) => ({ ...prev, fr: [...new Set([...(prev.fr || []), sug.texts.fr])] }))
      }
    } finally {
      setIsBusy(false)
    }
  }

  async function handleUnexclude(text: string) {
    setIsBusy(true)
    try {
      const res = await dispatch(removeBankQuestionExclusion_AsSupervisor({ bankId: props.bankId, text })).unwrap()
      if (res.error || !res.success) return // fetchBackend already toasted a real error

      setExcludedTexts((prev) => ({ ...prev, fr: (prev.fr || []).filter((t) => t !== text) }))
    } finally {
      setIsBusy(false)
    }
  }

  async function handleDelete() {
    setIsBusy(true)
    try {
      const res = await dispatch(deleteBankQuestion_AsSupervisor(props.bankId)).unwrap()
      setShowDeleteConfirm(false)
      if (res.error) return // fetchBackend already toasted the real error

      // Deleting now always succeeds — detaches any still-linked dim_questions (bank_id -> NULL)
      // instead of refusing (Tom/Ben, 2026-09-22: an illegitimate question should be removable
      // outright, not stuck because history already exists under it). detachedCount > 0 means
      // this dropped real archived history, not just an orphaned/never-archived row — worth its
      // own toast wording.
      toast(
        res.detachedCount > 0
          ? t("benchmark_editor_supervisor_deleted_detached_toast", { count: res.detachedCount })
          : t("benchmark_editor_supervisor_deleted_toast")
      )
      props.onChanged()
      props.onClose()
    } finally {
      setIsBusy(false)
    }
  }

  const excludedFr = excludedTexts.fr || []

  const allSectorsRow = benchmarkSummary.find((row) => row.sector === "all")
  const sectorBarRows = benchmarkSummary.filter((row) => row.sector !== "all").sort((a, b) => Number(b.value) - Number(a.value))
  const maxSectorValue = Math.max(...sectorBarRows.map((row) => Number(row.value)), 0)

  const usageMasterCount = templateUsages.filter((u) => u.fromSupervisor).length
  const usageDistinctAccounts = new Set(templateUsages.map((u) => u.accountId).filter(Boolean)).size

  return (
    <>
    <Modal
      disableKeyEvents={isCalculating || showCalculationSuccess}
      onClose={() => { if (!isBusy && !showCalculationSuccess) props.onClose() }}
      isFullScreen
      // Modal's own h1 is 24px globally (index.css) — a plain question can run long, and at that
      // size + full width it read as oversized here. Overriding via a wrapped span scoped to
      // this modal only, not the shared rule.
      title={
        <div className="flex flex-wrap items-center gap-2">
          <span style={{ fontSize: 18 }}>{props.title}</span>
          <span className="flex items-center text-sm font-normal">
            <BankQuestionTagChips isReverse={props.isReverse} scalePoints={props.scalePoints} />
          </span>
        </div>
      }
      status={isLoaded ? STATUS_LOADED : STATUS_LOADING}
      // isCloseButtonVisible not used here — that would place "Fermer" before this footer content
      // (Modal's own footer row order), not after it. Building the whole row instead: a left
      // spacer to balance the right one, scan/delete centered, close pushed to the end.
      footer={
        <div className="flex items-center" style={{ width: "100%" }}>
          <div style={{ flex: 1 }}>
            <Button icon={faCalculator} className={needsCalculation ? "primary" : "secondary"} disabled={!isLoaded || !needsCalculation || isBusy} isLoading={isCalculating} onClick={handleCalculate}>
              {t("benchmark_editor_supervisor_calculate_question")}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "links" && isScanOpen && checkedSuggestions.length > 0 ? (
              <>
                <Button icon={faLink} className="primary" disabled={isBusy} onClick={() => handleAssociate(checkedSuggestions)}>
                  {t("benchmark_editor_supervisor_associate_all")} ({checkedSuggestions.length})
                </Button>
                <Button icon={faBan} isWithBorder disabled={isBusy} onClick={() => handleExclude(checkedSuggestions)}>
                  {t("benchmark_editor_supervisor_exclude_all")} ({checkedSuggestions.length})
                </Button>
              </>
            ) : (
              <>
                <Button isWithBorder icon={faMagnifyingGlass} disabled={!isLoaded || isBusy} onClick={handleScan}>
                  {t("benchmark_editor_supervisor_scan")}
                </Button>
                <Button isWithBorder icon={faTrash} disabled={!isLoaded || isBusy} onClick={() => setShowDeleteConfirm(true)}>
                  {t("utils_delete")}
                </Button>
              </>
            )}
          </div>
          <div className="flex justify-end" style={{ flex: 1 }}>
            <Button isWithBorder disabled={isBusy} onClick={props.onClose}>{t("utils_close")}</Button>
          </div>
        </div>
      }>
      {/* position:relative here, not on .modal itself (shared CSS, not touched) — needed so the
          busy overlay below anchors to this content box instead of climbing up to
          .modal-container (position:fixed, full viewport) since .modal itself is static. */}
      <div style={{ position: "relative" }}>
        {/* Blocks every other action while one is in flight (found needed live, 2026-09-14 —
            associate/merge can take a moment, one request per language). A plain overlay div
            intercepts clicks on everything beneath it, no per-button disabled prop needed. */}
        {isBusy && <BusyOverlay text={isScanning ? t("benchmark_editor_supervisor_scan_filter_loading") : undefined} />}

        {props.tags && props.tags.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap" style={{ marginBottom: 12 }}>
            {props.tags.map((tag) => (
              <Chip key={tag} isWithMargin>
                {tagLabel(t, tag)}
              </Chip>
            ))}
          </div>
        )}


        {/* Not a tab — a small per-sector bar chart right under the title, always visible rather
            than needing a click. Replaced the icon-chip strip (2026-09-17) — a bar's length
            compares sectors against each other at a glance, which a row of same-size chips
            can't do. "all" pulled out as a headline number rather than one more bar since it's
            not comparable to a single sector's.
            Own Card + a responsive grid (2026-09-17) instead of one column — a single-column list
            left most of the fullscreen modal's width empty; auto-fill wraps as many bars per row
            as fit instead of stretching each one full-width (would make the fixed-width bar look
            lost next to a huge gap before the value). */}
        {benchmarkSummary.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Card>
              <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginBottom: 14 }}>
                <b>{t("benchmark_editor_supervisor_tab_benchmark")}</b>
                {allSectorsRow && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faLayerGroup} style={{ color: SECTOR_COLORS.all }} />
                    <b style={{ fontSize: 20 }}>{allSectorsRow.value}</b>
                    <span className="grey-t text-sm">
                      {t("benchmark_editor_supervisor_benchmark_chip_tooltip", {
                        sector: t("benchmark_editor_supervisor_benchmark_all_sectors"),
                        count: allSectorsRow.count
                      })}
                    </span>
                  </div>
                )}
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
                {sectorBarRows.map((row) => {
                  const value = Number(row.value)
                  const widthPct = maxSectorValue > 0 ? Math.max((value / maxSectorValue) * 100, 4) : 4
                  const color = SECTOR_COLORS[row.sector] || SECTOR_COLORS.other
                  return (
                    <Tooltip
                      key={row.sector}
                      text={t("benchmark_editor_supervisor_benchmark_chip_tooltip", {
                        sector: t("benchmark_sector_" + row.sector),
                        count: row.count
                      })}>
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={SECTOR_ICONS[row.sector] || faLayerGroup} fixedWidth style={{ color }} />
                        <span className="text-sm" style={{ flexShrink: 0, whiteSpace: "nowrap" }}>{t("benchmark_sector_" + row.sector)}</span>
                        <div style={{ flex: 1, backgroundColor: "#F0F0F0", borderRadius: 4, height: 10, overflow: "hidden" }}>
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${widthPct}%` }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            style={{ height: "100%", backgroundColor: color, borderRadius: 4 }}
                          />
                        </div>
                        <span className="text-sm" style={{ width: 36, textAlign: "right", flexShrink: 0 }}>
                          {row.value}
                        </span>
                      </div>
                    </Tooltip>
                  )
                })}
              </div>
            </Card>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <Tabs
            active={activeTab}
            onClick={(tab: Tab) => setActiveTab(tab)}
            tabs={{
              links: t("benchmark_editor_supervisor_tab_links"),
              exclusions: t("benchmark_editor_supervisor_tab_exclusions", { count: excludedFr.length }),
              usages: isTemplateUsagesLoaded
                ? t("benchmark_editor_supervisor_tab_usages", { count: templateUsages.length })
                : t("benchmark_editor_supervisor_tab_usages_loading")
            }}
          />
        </div>

        {activeTab === "links" && (
          <>
            {LANGUAGES.map((lang) => {
              const langTexts = texts[lang] || []
              if (langTexts.length === 0) return null
              return (
                <div key={lang}>
                  <div className="grey-t text-sm" style={{ padding: "8px 0 4px 4px" }}>
                    {LANGUAGE_FLAGS[lang]} {LANGUAGE_LABELS[lang]}
                  </div>
                  {langTexts.map((text, index) => {
                    const isPrimary = index === 0
                    return (
                      <ListItem key={text} isSmall className="items-center">
                        {/* Not ListCell's own `text` prop — its shared CSS forces single-line
                            white-space:nowrap, which overflows into a horizontal scrollbar instead of
                            wrapping for a long phrasing (found live, 2026-09-14). */}
                        <ListCell isFlex>
                          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{text}</div>
                        </ListCell>
                        <ListCell width={32} className="items-center">
                          {/* Nothing to pick between when there's only one phrasing in this language.
                              `color="#CCCCCC"` (an earlier attempt) actually fills a background box on
                              ListButton (backgroundColor + white icon, not a plain tint) — not what was
                              wanted, and not a component change either: `isInactive` already gives a
                              plain light-grey icon (#d8d8d8, no box, ListButton's own click() doesn't
                              gate on it — still clickable) without touching list-button.tsx at all. */}
                          {langTexts.length > 1 && !isPrimary && (
                            <ListButton
                              icon={faStar}
                              text={t("benchmark_editor_supervisor_set_primary")}
                              isInactive
                              onClick={() => handleSetPrimary(lang, text)}
                            />
                          )}
                          {langTexts.length > 1 && isPrimary && (
                            <ListButton icon={faStar} text={t("benchmark_editor_supervisor_is_primary")} isPrimary isColorActive />
                          )}
                        </ListCell>
                        <ListCell width={32} className="items-center">
                          {totalTexts > 1 && (
                            <ListButton icon={faUnlink} text={t("benchmark_editor_supervisor_unlink")} onClick={() => handleUnlink(lang, text)} />
                          )}
                        </ListCell>
                      </ListItem>
                    )
                  })}
                </div>
              )
            })}

            {isScanOpen && (
              <div style={{ marginTop: 16 }}>
                <Card isWithoutPadding className="mt-0">
                  {!isScanning && scanResults.length > 0 && (
                    <ListItem isSmall className="items-center">
                      <ListCell width={40} className="items-center">
                        <div>
                          <Checkbox
                            active={checkedSuggestions.length === scanResults.length}
                            disabled={isBusy}
                            onClick={() => setSelectedSuggestions(checkedSuggestions.length === scanResults.length
                              ? []
                              : scanResults.map(scanKey))}
                          />
                        </div>
                      </ListCell>
                      <ListCell isFlex>
                        <b>{t("benchmark_editor_supervisor_select_all")} ({scanResults.length})</b>
                      </ListCell>
                    </ListItem>
                  )}
                  {isScanning ? (
                    <div className="grey-t flex items-center justify-center" style={{ padding: "24px 16px" }}>
                      <FontAwesomeIcon icon={faCircleNotch} spin />
                    </div>
                  ) : scanResults.length === 0 ? (
                    <div className="grey-t text-sm" style={{ padding: "12px 16px" }}>
                      {t("benchmark_editor_supervisor_no_suggestion")}
                    </div>
                  ) : (
                    scanResults.map((sug) => (
                      <ListItem key={scanKey(sug)} isSmall isEditable className="items-center" onClick={() => toggleSuggestion(sug)}>
                        <ListCell width={40} className="items-center">
                          <div>
                            <Checkbox active={selectedSuggestions.includes(scanKey(sug))} onClick={() => toggleSuggestion(sug)} />
                          </div>
                        </ListCell>
                        <ListCell isFlex>
                          <div>
                            {LANGUAGES.filter((lang) => sug.texts[lang]).map((lang) => (
                              <div key={lang} className="flex items-center gap-1">
                                <span className="grey-t text-sm" style={{ minWidth: 24 }}>
                                  {LANGUAGE_FLAGS[lang]}
                                </span>
                                <span>{sug.texts[lang]}</span>
                              </div>
                            ))}
                            {/* Chip's own root div has no width constraint of its own — needs a
                                flex parent to size to content instead of stretching full-row
                                (found live, 2026-09-22). */}
                            <div className="flex items-center gap-1 flex-wrap" style={{ marginTop: 4 }}>
                              {/* app.Questions.reverse, read straight from the live question.
                                  Scale cardinality (2026-09-22, Ben) — associating a candidate
                                  into the eNPS row needs a real 10-point scale, nothing else in
                                  this screen showed it before, forcing a manual DB check every
                                  time. */}
                              <BankQuestionTagChips isReverse={sug.isReverse} scalePoints={sug.scalePoints} />
                            </div>
                          </div>
                        </ListCell>
                        <ListCell width={80} text={`${Math.round(sug.similarity * 100)}%`} />
                        <ListCell width={32} className="items-center">
                          <ListButton icon={faLink} text={t("benchmark_editor_supervisor_associate")} onClick={() => handleAssociate(sug)} />
                        </ListCell>
                        <ListCell width={32} className="items-center">
                          <ListButton icon={faBan} text={t("benchmark_editor_supervisor_exclude")} onClick={() => handleExclude(sug)} />
                        </ListCell>
                      </ListItem>
                    ))
                  )}
                </Card>
              </div>
            )}
          </>
        )}

        {activeTab === "exclusions" && (
          <>
            {excludedFr.length === 0 ? (
              <div className="grey-t text-sm" style={{ padding: "12px 4px" }}>
                {t("benchmark_editor_supervisor_no_exclusion")}
              </div>
            ) : (
              excludedFr.map((text) => (
                <ListItem key={text} isSmall className="items-center">
                  <ListCell isFlex>
                    <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{text}</div>
                  </ListCell>
                  <ListCell width={32} className="items-center">
                    <ListButton icon={faRotateLeft} text={t("benchmark_editor_supervisor_reintegrate")} onClick={() => handleUnexclude(text)} />
                  </ListCell>
                </ListItem>
              ))
            )}
          </>
        )}

        {/* Informational only, no 1-click jump to the editor: it only loads a
            template via Redux state from within its own page (no id-based URL), and a template
            belonging to a different account can't be opened at all without connecting as that
            account first. A raw list of 300+ template names isn't chart-worthy on its own (no
            numeric comparison in it), but the master-vs-client split and the distinct-account
            count are — a small summary up top that fits without scrolling, the capped list stays
            below for whoever needs the actual names. */}
        {activeTab === "usages" && (
          <div>
            {!isTemplateUsagesLoaded ? (
              <div className="grey-t flex items-center justify-center" style={{ padding: "24px 16px" }}>
                <FontAwesomeIcon icon={faCircleNotch} spin />
              </div>
            ) : templateUsages.length === 0 ? (
              <div className="grey-t text-sm" style={{ padding: "12px 4px" }}>
                {t("benchmark_editor_supervisor_no_usage")}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 16 }}>
                <Card>
                  <div className="flex items-center gap-4 flex-wrap" style={{ marginBottom: 14 }}>
                    <div>
                      <b style={{ fontSize: 20 }}>{templateUsages.length}</b>
                      <span className="grey-t text-sm"> {t("benchmark_editor_supervisor_usage_templates_label")}</span>
                    </div>
                    <div>
                      <b style={{ fontSize: 20 }}>{usageDistinctAccounts}</b>
                      <span className="grey-t text-sm"> {t("benchmark_editor_supervisor_usage_accounts_label")}</span>
                    </div>
                  </div>
                  {[
                    { label: t("benchmark_editor_supervisor_usage_master"), value: usageMasterCount, color: "#748FFC" },
                    { label: t("benchmark_editor_supervisor_usage_client"), value: templateUsages.length - usageMasterCount, color: "#69DB7C" }
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-2" style={{ marginTop: 6 }}>
                      <span className="text-sm" style={{ width: 170, flexShrink: 0 }}>{row.label}</span>
                      <div style={{ flex: 1, backgroundColor: "#F0F0F0", borderRadius: 4, height: 10, overflow: "hidden" }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${templateUsages.length > 0 ? (row.value / templateUsages.length) * 100 : 0}%` }}
                          transition={{ duration: 0.6, ease: "easeOut" }}
                          style={{ height: "100%", backgroundColor: row.color, borderRadius: 4 }}
                        />
                      </div>
                      <span className="text-sm" style={{ width: 30, textAlign: "right", flexShrink: 0 }}>{row.value}</span>
                    </div>
                  ))}
                </Card>
                </div>

                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                {(showAllUsages ? templateUsages : templateUsages.slice(0, 15)).map((usage) => (
                  <ListItem key={usage.templateId} isSmall className="items-center">
                    <ListCell isFlex>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{usage.templateName}</span>
                        <span className="flex items-center gap-1 text-sm grey-t">
                          <FontAwesomeIcon icon={faBuilding} fixedWidth />
                          {usage.accountName ?? t("benchmark_editor_supervisor_master_template")}
                        </span>
                      </div>
                    </ListCell>
                  </ListItem>
                ))}
                {!showAllUsages && templateUsages.length > 15 && (
                  <div style={{ padding: "8px 4px" }}>
                    <Link isWithoutMargin onClick={() => setShowAllUsages(true)}>
                      {t("benchmark_editor_supervisor_show_all_usages", { count: templateUsages.length })}
                    </Link>
                  </div>
                )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
    {showDeleteConfirm && (
      <ModalConfirm
        onNo={() => setShowDeleteConfirm(false)}
        onYes={handleDelete}
        text={t("benchmark_editor_supervisor_delete")}
        textBold={t("utils_next_ask")}
        status={isBusy ? STATUS_SAVING : undefined}
      />
    )}
    {isCalculating && <LoadingModal>{t("benchmark_editor_supervisor_calculating_question")}</LoadingModal>}
    {showCalculationSuccess && (
      <CongratulationModal
        text={t("benchmark_editor_supervisor_calculated_question")}
        onCloseText={t("utils_close")}
        onClose={() => setShowCalculationSuccess(false)}
      />
    )}
    </>
  )
}

export default BankQuestionManageModal
